/**
 * RL Loop Monitor
 *
 * Real-time monitoring component for active RL loops.
 * Shows progress, quality trends, and allows pause/resume/cancel actions.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  LinearProgress,
  Button,
  Chip,
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Pause as PauseIcon,
  PlayArrow as ResumeIcon,
  Stop as CancelIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Upgrade as PromoteIcon,
  AddCircleOutline as SaveAsNewIcon,
  Undo as RollbackIcon,
  Compress as ConsolidateIcon,
  BlockOutlined as FailedIcon,
  Psychology as PsychologyIcon,
  Lightbulb as LightbulbIcon,
  Build as BuildIcon,
  CompareArrows as CompareArrowsIcon,
  Visibility as ViewIcon,
  History as HistoryIcon,
  RestoreOutlined as RestoreIcon,
  ContentCopy as CloneIcon,
  Edit as EditIcon,
  Settings as ConfigIcon
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  ResponsiveContainer
} from 'recharts';
import {
  getRLLoopStatus,
  getRLLoopIterations,
  pauseRLLoop,
  resumeRLLoop,
  cancelRLLoop,
  promoteRLLoopPersonas,
  saveRLLoopAsNewPersona,
  getRLLoopPromptComparison,
  getPersonaVersions,
  getPersonaVersionDetail,
  rollbackPersonaVersion,
  getRLLoopSessionSummary,
  updateRLLoopConfig
} from '../../../services/rlLoopApi';

const STATUS_COLORS = {
  pending: 'default',
  running: 'primary',
  paused_by_user: 'warning',
  paused_for_code_change: 'warning',
  completed: 'success',
  failed: 'error',
  cancelled: 'default'
};

const STATUS_LABELS = {
  pending: 'Pending',
  running: 'Running',
  paused_by_user: 'Paused',
  paused_for_code_change: 'Paused (Code Changes Needed)',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled'
};

// Helper to generate a human-readable label from a dimension key
// e.g. "adhd_psychoeducation" → "Adhd Psychoeducation", "empathy" → "Empathy"
const dimensionKeyToLabel = (key) =>
  key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

// Helper function to get score color (scale-aware)
const getScoreColor = (score, scoringScale = 3) => {
  const ratio = score / scoringScale;
  if (ratio >= 0.85) return 'success';
  if (ratio >= 0.5) return 'warning';
  return 'error';
};

// Helper function to get score label (scale-aware)
const getScoreLabel = (score, scoringScale = 3) => {
  const ratio = score / scoringScale;
  if (ratio >= 0.85) return 'Excellent';
  if (ratio >= 0.5) return 'Good';
  if (ratio >= 0.25) return 'Needs Work';
  return 'Critical';
};

/**
 * QualityDimensionBreakdown - Shows score bars for each quality dimension
 */
function QualityDimensionBreakdown({ qualityBreakdown, scoringScale = 3 }) {
  const [expandedDimension, setExpandedDimension] = useState(null);

  if (!qualityBreakdown || Object.keys(qualityBreakdown).length === 0) {
    return null;
  }

  // Dynamically read dimension keys from the data instead of hardcoded list
  const dimensionKeys = Object.keys(qualityBreakdown);

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <PsychologyIcon fontSize="small" />
        Quality Breakdown by Dimension
      </Typography>
      <Paper variant="outlined" sx={{ p: 1 }}>
        {dimensionKeys.map((key) => {
          const dimension = qualityBreakdown[key];
          if (!dimension) return null;

          // Use per-dimension maxScore if available, otherwise fall back to review-level scoringScale
          const dimMax = dimension.maxScore || scoringScale;
          const label = dimensionKeyToLabel(key);
          const score = dimension.score || 0;
          const progress = Math.min((score / dimMax) * 100, 100);
          const color = getScoreColor(score, dimMax);
          const dimScoreLabel = getScoreLabel(score, dimMax);
          const isExpanded = expandedDimension === key;

          return (
            <Accordion
              key={key}
              expanded={isExpanded}
              onChange={() => setExpandedDimension(isExpanded ? null : key)}
              sx={{
                '&:before': { display: 'none' },
                boxShadow: 'none',
                bgcolor: score <= 1 ? 'error.lighter' : 'transparent'
              }}
              disableGutters
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ minHeight: 'auto', '& .MuiAccordionSummary-content': { my: 0.5 } }}
              >
                <Grid container alignItems="center" spacing={1}>
                  <Grid item xs={3}>
                    <Typography variant="body2" sx={{ fontWeight: score <= 1 ? 600 : 400 }}>
                      {label}
                      {score <= 1 && ' ⚠️'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <LinearProgress
                      variant="determinate"
                      value={progress}
                      color={color}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="body2" color={`${color}.main`} sx={{ fontWeight: 500 }}>
                      {score}/{dimMax} {dimScoreLabel}
                    </Typography>
                  </Grid>
                </Grid>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                {dimension.evidence && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">Evidence:</Typography>
                    <Typography variant="body2" sx={{
                      bgcolor: 'grey.100',
                      p: 1,
                      borderRadius: 1,
                      fontStyle: 'italic',
                      fontSize: '0.85rem'
                    }}>
                      "{dimension.evidence}"
                    </Typography>
                  </Box>
                )}
                {dimension.improvement && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Suggested Improvement:</Typography>
                    <Typography variant="body2" color="primary.main" sx={{ fontSize: '0.85rem' }}>
                      {dimension.improvement}
                    </Typography>
                  </Box>
                )}
                {dimension.calibrationNote && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">Calibration Note:</Typography>
                    <Typography variant="body2" sx={{
                      bgcolor: 'warning.lighter',
                      p: 1,
                      borderRadius: 1,
                      fontSize: '0.85rem'
                    }}>
                      {dimension.calibrationNote}
                    </Typography>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Paper>
    </Box>
  );
}

/**
 * RecommendationsSection - Shows recommendations and next iteration focus
 */
function RecommendationsSection({ recommendations, nextIterationFocus }) {
  if (!recommendations && !nextIterationFocus) {
    return null;
  }

  return (
    <Box sx={{ mt: 2 }}>
      {recommendations && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <LightbulbIcon fontSize="small" color="warning" />
            Recommendations
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'warning.lighter' }}>
            <Typography variant="body2">{recommendations}</Typography>
          </Paper>
        </Box>
      )}
      {nextIterationFocus && (
        <Alert severity="info" icon={<BuildIcon />}>
          <AlertTitle>Focus for Next Iteration</AlertTitle>
          <Typography variant="body2">{nextIterationFocus}</Typography>
        </Alert>
      )}
    </Box>
  );
}

/**
 * CalibrationIssuesSection - Shows calibration issues from client persona realism review
 */
function CalibrationIssuesSection({ calibrationIssues }) {
  if (!calibrationIssues || calibrationIssues.length === 0) return null;

  const getSeverityColor = (severity) => {
    if (severity === 'critical' || severity === 'high') return 'error';
    if (severity === 'medium') return 'warning';
    return 'info';
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Calibration Issues ({calibrationIssues.length})
      </Typography>
      {calibrationIssues.map((issue, i) => (
        <Alert key={i} severity={getSeverityColor(issue.severity)} sx={{ mb: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            {issue.issue}
          </Typography>
          {issue.parameterPath && (
            <Typography variant="caption" sx={{ display: 'block' }}>
              Parameter: <code>{issue.parameterPath}</code>
            </Typography>
          )}
          {issue.observed && (
            <Typography variant="caption" sx={{ display: 'block' }}>
              Observed: {issue.observed}
            </Typography>
          )}
          {issue.expected && (
            <Typography variant="caption" sx={{ display: 'block' }}>
              Expected: {issue.expected}
            </Typography>
          )}
        </Alert>
      ))}
    </Box>
  );
}

/**
 * PersonaImprovementsSection - Shows persona improvement suggestions from client realism review
 */
function PersonaImprovementsSection({ personaImprovements }) {
  if (!personaImprovements || personaImprovements.length === 0) return null;

  const getPriorityColor = (priority) => {
    if (priority === 'critical' || priority === 'high') return 'error';
    if (priority === 'medium') return 'warning';
    return 'info';
  };

  // Group by targetType
  const grouped = personaImprovements.reduce((acc, imp) => {
    const key = imp.targetType || 'general';
    if (!acc[key]) acc[key] = [];
    acc[key].push(imp);
    return acc;
  }, {});

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Persona Improvement Suggestions ({personaImprovements.length})
      </Typography>
      {Object.entries(grouped).map(([targetType, improvements]) => (
        <Box key={targetType} sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 'bold' }}>
            {targetType.replace(/_/g, ' ')}
          </Typography>
          {improvements.map((imp, i) => (
            <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                {imp.priority && (
                  <Chip
                    label={imp.priority.toUpperCase()}
                    size="small"
                    color={getPriorityColor(imp.priority)}
                    variant="outlined"
                  />
                )}
                {imp.parameterPath && (
                  <Typography variant="caption" color="text.secondary">
                    <code>{imp.parameterPath}</code>
                  </Typography>
                )}
              </Box>
              {imp.suggestedChange && (
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  {imp.suggestedChange}
                </Typography>
              )}
              {imp.currentValue && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Current: {typeof imp.currentValue === 'object' ? JSON.stringify(imp.currentValue) : imp.currentValue}
                </Typography>
              )}
              {imp.reasoning && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic', mt: 0.5 }}>
                  {imp.reasoning}
                </Typography>
              )}
            </Paper>
          ))}
        </Box>
      ))}
    </Box>
  );
}

/**
 * ImprovementDetailDialog - Shows full improvement details with side-by-side comparison
 */
function ImprovementDetailDialog({ open, onClose, improvement }) {
  if (!improvement) return null;

  const getCategoryColor = (category) => {
    const colors = {
      safety_enhancement: 'error',
      technique_suggestion: 'primary',
      empathy_improvement: 'success',
      engagement_boost: 'info',
      boundary_clarification: 'warning',
      pacing_adjustment: 'secondary'
    };
    return colors[category] || 'default';
  };

  const getPriorityColor = (priority) => {
    if (priority === 'high' || priority === 'critical') return 'error';
    if (priority === 'medium') return 'warning';
    return 'info';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CompareArrowsIcon />
        Improvement Details
      </DialogTitle>
      <DialogContent dividers>
        {/* Category and Priority */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Chip
            label={improvement.category?.replace(/_/g, ' ')?.toUpperCase() || 'IMPROVEMENT'}
            color={getCategoryColor(improvement.category)}
            size="small"
          />
          {improvement.priority && (
            <Chip
              label={`Priority: ${improvement.priority.toUpperCase()}`}
              color={getPriorityColor(improvement.priority)}
              size="small"
              variant="outlined"
            />
          )}
        </Box>

        {/* Side-by-side comparison */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%', bgcolor: 'error.lighter' }}>
              <Typography variant="subtitle2" color="error.main" gutterBottom>
                Current Text (Before)
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  fontSize: '0.85rem'
                }}
              >
                {improvement.currentText || improvement.originalText || '(Not available)'}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%', bgcolor: 'success.lighter' }}>
              <Typography variant="subtitle2" color="success.main" gutterBottom>
                Suggested Text (After)
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  fontSize: '0.85rem'
                }}
              >
                {improvement.suggestedText || improvement.newText || '(Not available)'}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Reasoning */}
        {improvement.reasoning && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Reasoning</Typography>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="body2">{improvement.reasoning}</Typography>
            </Paper>
          </Box>
        )}

        {/* Change description */}
        {improvement.changeDescription && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Change Description</Typography>
            <Typography variant="body2" color="text.secondary">
              {improvement.changeDescription}
            </Typography>
          </Box>
        )}

        {/* Applied info */}
        <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {improvement.personaKey && (
            <Typography variant="caption" color="text.secondary">
              Persona: <strong>{improvement.personaKey}</strong>
            </Typography>
          )}
          {improvement.appliedAt && (
            <Typography variant="caption" color="text.secondary">
              Applied: {new Date(improvement.appliedAt).toLocaleString()}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * PromptComparisonDialog - Shows sandbox vs original prompt side by side
 */
function PromptComparisonDialog({
  open,
  onClose,
  comparisons,
  loopName,
  loading,
  onViewVersionHistory,
  token
}) {
  const [selectedPersona, setSelectedPersona] = useState(0);

  if (!open) return null;

  const currentComparison = comparisons?.[selectedPersona];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ViewIcon />
          Prompt Comparison - {loopName}
        </Box>
        {comparisons?.length > 1 && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {comparisons.map((comp, i) => (
              <Chip
                key={i}
                label={comp.personaKey}
                onClick={() => setSelectedPersona(i)}
                color={selectedPersona === i ? 'primary' : 'default'}
                size="small"
              />
            ))}
          </Box>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ p: 3 }}>
            <LinearProgress />
            <Typography sx={{ mt: 2 }}>Loading prompt comparison...</Typography>
          </Box>
        ) : !currentComparison ? (
          <Alert severity="info">No prompt comparisons available for this loop.</Alert>
        ) : (
          <>
            {/* Status indicators */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Chip
                label={currentComparison.hasChanges ? 'Prompts Differ' : 'Prompts Identical'}
                color={currentComparison.hasChanges ? 'warning' : 'success'}
                size="small"
              />
              <Typography variant="caption" color="text.secondary">
                Sandbox: {currentComparison.sandboxName || currentComparison.sandboxKey}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Original: {currentComparison.originalName || currentComparison.personaKey}
              </Typography>
            </Box>

            {/* Side-by-side comparison */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    height: '60vh',
                    overflow: 'auto',
                    bgcolor: 'success.lighter'
                  }}
                >
                  <Typography variant="subtitle2" color="success.main" gutterBottom>
                    Sandbox (Current - Being Improved)
                  </Typography>
                  {currentComparison.sandboxUpdatedAt && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Last updated: {new Date(currentComparison.sandboxUpdatedAt).toLocaleString()}
                    </Typography>
                  )}
                  <Divider sx={{ mb: 1 }} />
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      fontSize: '0.8rem',
                      lineHeight: 1.5
                    }}
                  >
                    {currentComparison.sandboxPrompt || '(No sandbox prompt available)'}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    height: '60vh',
                    overflow: 'auto',
                    bgcolor: 'grey.100'
                  }}
                >
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Original (Live Persona)
                  </Typography>
                  {currentComparison.originalUpdatedAt && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Last updated: {new Date(currentComparison.originalUpdatedAt).toLocaleString()}
                    </Typography>
                  )}
                  <Divider sx={{ mb: 1 }} />
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      fontSize: '0.8rem',
                      lineHeight: 1.5
                    }}
                  >
                    {currentComparison.originalPrompt || '(No original prompt available)'}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Character count comparison */}
            <Box sx={{ mt: 2, display: 'flex', gap: 3, alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Sandbox: {currentComparison.sandboxPrompt?.length || 0} chars
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Original: {currentComparison.originalPrompt?.length || 0} chars
              </Typography>
              {currentComparison.sandboxPrompt && currentComparison.originalPrompt && (
                <Typography
                  variant="caption"
                  color={currentComparison.sandboxPrompt.length > currentComparison.originalPrompt.length ? 'warning.main' : 'success.main'}
                >
                  Delta: {currentComparison.sandboxPrompt.length - currentComparison.originalPrompt.length} chars
                </Typography>
              )}
              <Box sx={{ flexGrow: 1 }} />
              {onViewVersionHistory && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<HistoryIcon />}
                  onClick={() => onViewVersionHistory(currentComparison)}
                >
                  View Version History
                </Button>
              )}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * VersionHistoryDialog - Shows version history for a persona with rollback capability
 */
function VersionHistoryDialog({
  open,
  onClose,
  personaId,
  personaType,
  personaKey,
  token,
  onRollbackSuccess
}) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [versionDetail, setVersionDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [rollbackConfirmOpen, setRollbackConfirmOpen] = useState(false);
  const [rollbackLoading, setRollbackLoading] = useState(false);

  // Fetch versions when dialog opens
  useEffect(() => {
    if (open && personaId) {
      fetchVersions();
    }
  }, [open, personaId]);

  const fetchVersions = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getPersonaVersions(personaId, token);
      setVersions(result.versions || []);
    } catch (err) {
      setError(err.error || 'Failed to load version history');
    } finally {
      setLoading(false);
    }
  };

  const handleViewVersion = async (version) => {
    setSelectedVersion(version);
    setDetailLoading(true);
    try {
      const detail = await getPersonaVersionDetail(personaId, version.version, token);
      setVersionDetail(detail);
    } catch (err) {
      setError(err.error || 'Failed to load version details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleRollbackClick = (version) => {
    setSelectedVersion(version);
    setRollbackConfirmOpen(true);
  };

  const handleRollbackConfirm = async () => {
    if (!selectedVersion) return;

    setRollbackLoading(true);
    try {
      await rollbackPersonaVersion(personaId, selectedVersion.version, personaType, token);
      setRollbackConfirmOpen(false);
      fetchVersions(); // Refresh version list
      if (onRollbackSuccess) {
        onRollbackSuccess(selectedVersion.version);
      }
    } catch (err) {
      setError(err.error || 'Failed to rollback to version');
    } finally {
      setRollbackLoading(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <HistoryIcon />
        Version History - {personaKey || 'Persona'}
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ p: 3 }}>
            <LinearProgress />
            <Typography sx={{ mt: 2 }}>Loading version history...</Typography>
          </Box>
        ) : versions.length === 0 ? (
          <Alert severity="info">No version history available for this persona.</Alert>
        ) : (
          <Grid container spacing={2}>
            {/* Version List */}
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>
                {versions.length} Version(s)
              </Typography>
              <Paper variant="outlined" sx={{ maxHeight: '50vh', overflow: 'auto' }}>
                <List dense>
                  {versions.map((v) => (
                    <ListItem
                      key={v.version}
                      button
                      selected={selectedVersion?.version === v.version}
                      onClick={() => handleViewVersion(v)}
                      sx={{
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      <ListItemIcon>
                        {v.changeDescription?.includes('Pre-promotion backup') ? (
                          <Tooltip title="Pre-promotion backup">
                            <WarningIcon color="warning" fontSize="small" />
                          </Tooltip>
                        ) : v.changeDescription?.includes('Promoted from') ? (
                          <Tooltip title="Promoted version">
                            <PromoteIcon color="success" fontSize="small" />
                          </Tooltip>
                        ) : v.changeDescription?.includes('Rolled back') ? (
                          <Tooltip title="Rollback">
                            <RestoreIcon color="info" fontSize="small" />
                          </Tooltip>
                        ) : (
                          <HistoryIcon fontSize="small" color="action" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" fontWeight={500}>
                              v{v.version}
                            </Typography>
                            <Chip
                              label={v.promptHash?.substring(0, 8)}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="caption" display="block" color="text.secondary">
                              {new Date(v.createdAt).toLocaleString()}
                            </Typography>
                            <Typography variant="caption" display="block" noWrap sx={{ maxWidth: 180 }}>
                              {v.changeDescription || 'No description'}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>

            {/* Version Detail */}
            <Grid item xs={12} md={8}>
              {!selectedVersion ? (
                <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                  <HistoryIcon sx={{ fontSize: 48, opacity: 0.5 }} />
                  <Typography sx={{ mt: 1 }}>Select a version to view details</Typography>
                </Box>
              ) : detailLoading ? (
                <Box sx={{ p: 3 }}>
                  <LinearProgress />
                  <Typography sx={{ mt: 2 }}>Loading version details...</Typography>
                </Box>
              ) : versionDetail ? (
                <Box>
                  {/* Version header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box>
                      <Typography variant="h6">Version {versionDetail.version}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(versionDetail.createdAt).toLocaleString()} • {versionDetail.snapshot?.systemPrompt?.length || 0} chars
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      color="warning"
                      size="small"
                      startIcon={<RestoreIcon />}
                      onClick={() => handleRollbackClick(selectedVersion)}
                    >
                      Rollback to This
                    </Button>
                  </Box>

                  {/* Change description */}
                  {versionDetail.changeDescription && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <strong>Change:</strong> {versionDetail.changeDescription}
                    </Alert>
                  )}

                  {/* Full prompt */}
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      maxHeight: '40vh',
                      overflow: 'auto',
                      bgcolor: 'grey.50'
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        fontSize: '0.8rem',
                        lineHeight: 1.5
                      }}
                    >
                      {versionDetail.snapshot?.systemPrompt || '(No prompt content)'}
                    </Typography>
                  </Paper>
                </Box>
              ) : null}
            </Grid>
          </Grid>
        )}

        {/* Rollback Confirmation Dialog */}
        <Dialog
          open={rollbackConfirmOpen}
          onClose={() => setRollbackConfirmOpen(false)}
        >
          <DialogTitle>Confirm Rollback</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This will restore the persona prompt to version {selectedVersion?.version}.
              A backup of the current state will be created automatically.
            </Alert>
            <Typography variant="body2">
              <strong>Change description:</strong> {selectedVersion?.changeDescription || 'N/A'}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Created:</strong> {selectedVersion?.createdAt ? new Date(selectedVersion.createdAt).toLocaleString() : 'N/A'}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRollbackConfirmOpen(false)} disabled={rollbackLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleRollbackConfirm}
              variant="contained"
              color="warning"
              disabled={rollbackLoading}
            >
              {rollbackLoading ? 'Rolling back...' : 'Confirm Rollback'}
            </Button>
          </DialogActions>
        </Dialog>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * SessionSummary - Comprehensive analysis displayed when loop is completed/stopped
 * Uses the new sessionSummary field with LLM-generated insights
 */
function SessionSummary({ loop, iterations, token }) {
  const [expandedSection, setExpandedSection] = useState(null);
  const [sessionSummary, setSessionSummary] = useState(loop?.sessionSummary || null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [summaryError, setSummaryError] = useState(null);

  // Fetch session summary if not already present and loop is completed
  useEffect(() => {
    const fetchSessionSummary = async () => {
      if (!['completed', 'stopped', 'cancelled', 'failed'].includes(loop?.status)) return;
      if (sessionSummary?.generatedAt) return; // Already have it

      setLoadingSummary(true);
      try {
        const result = await getRLLoopSessionSummary(loop._id, false, token);
        setSessionSummary(result.sessionSummary);
      } catch (err) {
        console.error('Failed to fetch session summary:', err);
        setSummaryError(err.message || 'Failed to load session summary');
      } finally {
        setLoadingSummary(false);
      }
    };

    fetchSessionSummary();
  }, [loop?._id, loop?.status, sessionSummary?.generatedAt, token]);

  if (!['completed', 'stopped', 'cancelled', 'failed'].includes(loop?.status)) {
    return null;
  }

  // Fallback to basic metrics if no session summary
  const summary = sessionSummary || {};
  const qualityJourney = summary.qualityJourney || {
    startingQuality: loop.metrics?.startingQuality || 0,
    endingQuality: loop.metrics?.currentQuality || 0,
    bestQuality: loop.metrics?.bestQuality || 0,
    netImprovement: (loop.metrics?.currentQuality || 0) - (loop.metrics?.startingQuality || 0)
  };
  const improvementAnalysis = summary.improvementAnalysis || {};
  const convergenceBehavior = summary.convergenceBehavior || {};
  const recommendations = summary.recommendations || [];
  const overallAssessment = summary.overallAssessment || '';
  const keyFindings = summary.keyFindings || [];
  const suitabilityScore = summary.suitabilityScore;
  const iterationSummaries = summary.iterationSummaries || [];

  const qualityDelta = qualityJourney.netImprovement || 0;
  const qualityDeltaPercent = qualityJourney.netImprovementPercent ||
    (qualityJourney.startingQuality > 0
      ? ((qualityDelta / qualityJourney.startingQuality) * 100).toFixed(1)
      : 0);

  const handleRegenerateSummary = async () => {
    setLoadingSummary(true);
    setSummaryError(null);
    try {
      const result = await getRLLoopSessionSummary(loop._id, true, token);
      setSessionSummary(result.sessionSummary);
    } catch (err) {
      setSummaryError(err.message || 'Failed to regenerate summary');
    } finally {
      setLoadingSummary(false);
    }
  };

  return (
    <Paper sx={{ p: 2, mb: 2, bgcolor: loop.status === 'completed' ? 'success.lighter' : 'warning.lighter' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {loop.status === 'completed' ? <CheckIcon color="success" /> : <WarningIcon color="warning" />}
          Session Summary
          {suitabilityScore !== null && suitabilityScore !== undefined && (
            <Chip
              label={`RL Suitability: ${suitabilityScore}%`}
              size="small"
              color={suitabilityScore >= 60 ? 'success' : suitabilityScore >= 40 ? 'warning' : 'error'}
            />
          )}
        </Typography>
        <Button
          size="small"
          onClick={handleRegenerateSummary}
          disabled={loadingSummary}
          startIcon={<RefreshIcon />}
        >
          {loadingSummary ? 'Generating...' : 'Regenerate'}
        </Button>
      </Box>

      {summaryError && (
        <Alert severity="error" sx={{ mb: 2 }}>{summaryError}</Alert>
      )}

      {/* Quick Stats */}
      <Grid container spacing={2}>
        <Grid item xs={6} md={3}>
          <Box sx={{ textAlign: 'center', p: 1 }}>
            <Typography variant="caption" color="text.secondary">Quality Journey</Typography>
            <Typography variant="h5" color={qualityDelta >= 0 ? 'success.main' : 'error.main'}>
              {qualityJourney.startingQuality?.toFixed(1)} → {qualityJourney.endingQuality?.toFixed(1)}
            </Typography>
            <Chip
              label={`${qualityDelta >= 0 ? '+' : ''}${qualityDeltaPercent}%`}
              color={qualityDelta >= 0 ? 'success' : 'error'}
              size="small"
            />
          </Box>
        </Grid>
        <Grid item xs={6} md={3}>
          <Box sx={{ textAlign: 'center', p: 1 }}>
            <Typography variant="caption" color="text.secondary">Best Quality</Typography>
            <Typography variant="h5" color="primary.main">
              {qualityJourney.bestQuality?.toFixed(1) || '-'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Iteration {qualityJourney.bestIterationNumber || '-'}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} md={3}>
          <Box sx={{ textAlign: 'center', p: 1 }}>
            <Typography variant="caption" color="text.secondary">Improvements</Typography>
            <Typography variant="h5">
              {improvementAnalysis.totalApplied || 0}
              <Typography component="span" variant="caption" color="error.main">
                {improvementAnalysis.totalReverted > 0 && ` (-${improvementAnalysis.totalReverted})`}
              </Typography>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {improvementAnalysis.retentionRate || 0}% retained
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} md={3}>
          <Box sx={{ textAlign: 'center', p: 1 }}>
            <Typography variant="caption" color="text.secondary">Regressions</Typography>
            <Typography variant="h5" color={qualityJourney.regressionCount > 0 ? 'error.main' : 'success.main'}>
              {qualityJourney.regressionCount || 0}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {convergenceBehavior.convergencePattern || 'N/A'}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* Near-Optimal Warning */}
      {qualityJourney.startedNearOptimal && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>Near-Optimal Starting Point</AlertTitle>
          The prompt started at {qualityJourney.startingQuality?.toFixed(1)}% quality, which is already near-optimal.
          RL Loops have limited improvement potential for already-optimized prompts.
        </Alert>
      )}

      {/* LLM Assessment */}
      {overallAssessment && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LightbulbIcon fontSize="small" />
            Assessment
          </Typography>
          <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.default' }}>
            <Typography variant="body2">{overallAssessment}</Typography>
          </Paper>
        </Box>
      )}

      {/* Key Findings */}
      {keyFindings.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Key Findings</Typography>
          <List dense disablePadding>
            {keyFindings.map((finding, i) => (
              <ListItem key={i} sx={{ py: 0 }}>
                <ListItemIcon sx={{ minWidth: 24 }}>
                  <Typography variant="body2" color="primary">{i + 1}.</Typography>
                </ListItemIcon>
                <ListItemText primary={finding} primaryTypographyProps={{ variant: 'body2' }} />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* Trade-off Pattern */}
      {improvementAnalysis.tradeoffPattern && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Trade-off Pattern Detected</AlertTitle>
          {improvementAnalysis.tradeoffPattern}
        </Alert>
      )}

      {/* Iteration Summary Table */}
      {iterationSummaries.length > 0 && (
        <Accordion
          expanded={expandedSection === 'iterations'}
          onChange={() => setExpandedSection(expandedSection === 'iterations' ? null : 'iterations')}
          sx={{ mb: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Iteration-by-Iteration Summary</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #ccc' }}>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Iter</th>
                    <th style={{ padding: '8px', textAlign: 'right' }}>Score</th>
                    <th style={{ padding: '8px', textAlign: 'center' }}>Status</th>
                    <th style={{ padding: '8px', textAlign: 'center' }}>Improvements</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Key Changes</th>
                  </tr>
                </thead>
                <tbody>
                  {iterationSummaries.map((iter, i) => (
                    <tr key={i} style={{
                      borderBottom: '1px solid #eee',
                      backgroundColor: iter.wasRollback ? '#fff3e0' : 'transparent'
                    }}>
                      <td style={{ padding: '8px' }}>{iter.iterationNumber}</td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>
                        {iter.weightedQualityScore?.toFixed(1) || '-'}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        {iter.wasRollback && <Chip label="Rolled Back" size="small" color="warning" />}
                        {iter.wasConsolidation && <Chip label="Consolidation" size="small" color="info" />}
                        {!iter.wasRollback && !iter.wasConsolidation && (
                          <Chip label="Applied" size="small" color="success" variant="outlined" />
                        )}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        {iter.improvementsApplied || 0}
                        {iter.improvementsReverted > 0 && (
                          <Typography component="span" color="error.main" variant="caption">
                            {' '}(-{iter.improvementsReverted})
                          </Typography>
                        )}
                      </td>
                      <td style={{ padding: '8px', fontSize: '0.75rem' }}>
                        {iter.keyDimensionChanges || iter.rollbackReason || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BuildIcon fontSize="small" />
            Recommendations
          </Typography>
          <List dense disablePadding>
            {recommendations.map((rec, i) => (
              <ListItem key={i} sx={{ py: 0.5, alignItems: 'flex-start' }}>
                <ListItemIcon sx={{ minWidth: 24, mt: 0.5 }}>
                  <CheckIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText primary={rec} primaryTypographyProps={{ variant: 'body2' }} />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* Promoted personas */}
      {loop.hasPromotedPersonas && (
        <Alert severity="success" sx={{ mt: 2 }}>
          <AlertTitle>Personas Promoted to Production</AlertTitle>
          Improved prompts have been promoted to live personas.
        </Alert>
      )}
    </Paper>
  );
}

function RLLoopMonitor({ loopId, token, onClose, onClone }) {
  const [loop, setLoop] = useState(null);
  const [iterations, setIterations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Dialog states
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [resumeDialogOpen, setResumeDialogOpen] = useState(false);

  // Edit focus dialog
  const [editFocusOpen, setEditFocusOpen] = useState(false);
  const [editFocusText, setEditFocusText] = useState('');
  const [editFocusSaving, setEditFocusSaving] = useState(false);
  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false);
  const [saveAsNewDialogOpen, setSaveAsNewDialogOpen] = useState(false);
  const [saveAsNewKey, setSaveAsNewKey] = useState('');
  const [saveAsNewName, setSaveAsNewName] = useState('');
  const [saveAsNewDescription, setSaveAsNewDescription] = useState('');
  const [saveAsNewError, setSaveAsNewError] = useState('');
  const [saveAsNewLoading, setSaveAsNewLoading] = useState(false);
  const [pauseReason, setPauseReason] = useState('');
  const [resumeNotes, setResumeNotes] = useState('');

  // Expanded iteration
  const [expandedIteration, setExpandedIteration] = useState(null);

  // Selected improvement for detail dialog
  const [selectedImprovement, setSelectedImprovement] = useState(null);

  // Prompt comparison dialog
  const [promptComparisonOpen, setPromptComparisonOpen] = useState(false);
  const [promptComparisons, setPromptComparisons] = useState([]);
  const [promptComparisonLoading, setPromptComparisonLoading] = useState(false);

  // Version history dialog
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [versionHistoryPersona, setVersionHistoryPersona] = useState(null);

  // Fetch prompt comparison
  const fetchPromptComparison = useCallback(async () => {
    setPromptComparisonLoading(true);
    try {
      const result = await getRLLoopPromptComparison(loopId, token);
      setPromptComparisons(result.comparisons || []);
    } catch (err) {
      console.error('Error fetching prompt comparison:', err);
      setError(err.error || 'Failed to fetch prompt comparison');
    } finally {
      setPromptComparisonLoading(false);
    }
  }, [loopId, token]);

  const handleViewPrompt = () => {
    setPromptComparisonOpen(true);
    fetchPromptComparison();
  };

  // Handler for viewing version history from prompt comparison dialog
  const handleViewVersionHistory = (comparison) => {
    // Get the original (live) persona ID from the loop config
    const targetPersona = loop?.config?.targetPersonas?.find(
      t => t.personaKey === comparison.personaKey
    );

    if (targetPersona?.originalLivePersonaId) {
      setVersionHistoryPersona({
        personaId: targetPersona.originalLivePersonaId,
        personaType: targetPersona.personaType || 'therapist',
        personaKey: comparison.personaKey
      });
      setVersionHistoryOpen(true);
    }
  };

  // Handler for successful rollback
  const handleRollbackSuccess = (versionNumber) => {
    // Refresh prompt comparison to show updated state
    fetchPromptComparison();
    // Optionally refresh loop data as well
    fetchData();
  };

  // Fetch loop data
  const fetchData = useCallback(async () => {
    try {
      const [statusRes, iterRes] = await Promise.all([
        getRLLoopStatus(loopId, token),
        getRLLoopIterations(loopId, token)
      ]);
      setLoop(statusRes);
      setIterations(iterRes.iterations || []);
      setError(null);
    } catch (err) {
      setError(err.error || 'Failed to fetch loop data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [loopId, token]);

  // Initial load and polling for active loops
  useEffect(() => {
    fetchData();

    // Poll every 5 seconds if loop is running
    const interval = setInterval(() => {
      if (loop?.status === 'running') {
        setRefreshing(true);
        fetchData();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [fetchData, loop?.status]);

  const handlePause = async () => {
    try {
      await pauseRLLoop(loopId, pauseReason, token);
      setPauseDialogOpen(false);
      setPauseReason('');
      fetchData();
    } catch (err) {
      setError(err.error || 'Failed to pause loop');
    }
  };

  const handleResume = async () => {
    try {
      await resumeRLLoop(loopId, resumeNotes, token);
      setResumeDialogOpen(false);
      setResumeNotes('');
      fetchData();
    } catch (err) {
      setError(err.error || 'Failed to resume loop');
    }
  };

  const handleCancel = async () => {
    if (window.confirm('Are you sure you want to cancel this loop?')) {
      try {
        await cancelRLLoop(loopId, token);
        fetchData();
      } catch (err) {
        setError(err.error || 'Failed to cancel loop');
      }
    }
  };

  const handlePromote = async () => {
    try {
      await promoteRLLoopPersonas(loopId, [], token);
      setPromoteDialogOpen(false);
      fetchData();
    } catch (err) {
      setError(err.error || 'Failed to promote personas');
    }
  };

  const handleOpenSaveAsNew = () => {
    const firstTarget = loop.config?.targetPersonas?.[0];
    const originalName = firstTarget?.personaKey || '';
    setSaveAsNewKey('');
    setSaveAsNewName('');
    setSaveAsNewDescription(`Derived from ${originalName} via RL Loop: ${loop.name || ''}`);
    setSaveAsNewError('');
    setSaveAsNewDialogOpen(true);
  };

  const handleSaveAsNew = async () => {
    // Client-side validation
    if (!saveAsNewKey || !saveAsNewName) {
      setSaveAsNewError('Key and name are required');
      return;
    }
    if (saveAsNewKey.length < 3) {
      setSaveAsNewError('Key must be at least 3 characters');
      return;
    }
    if (!/^[a-z0-9][a-z0-9-]*$/.test(saveAsNewKey)) {
      setSaveAsNewError('Key must start with a letter/number and contain only lowercase letters, numbers, and hyphens');
      return;
    }
    if (saveAsNewName.length < 2) {
      setSaveAsNewError('Name must be at least 2 characters');
      return;
    }

    const firstTarget = loop.config?.targetPersonas?.[0];
    if (!firstTarget) {
      setSaveAsNewError('No target persona found in loop config');
      return;
    }

    setSaveAsNewLoading(true);
    setSaveAsNewError('');
    try {
      await saveRLLoopAsNewPersona(loopId, {
        personaKey: firstTarget.personaKey,
        newKey: saveAsNewKey,
        newName: saveAsNewName,
        newDescription: saveAsNewDescription || undefined
      }, token);
      setSaveAsNewDialogOpen(false);
      setSaveAsNewKey('');
      setSaveAsNewName('');
      setSaveAsNewDescription('');
      fetchData();
    } catch (err) {
      const errorMsg = err.error || err.message || 'Failed to save as new persona';
      setSaveAsNewError(errorMsg);
    } finally {
      setSaveAsNewLoading(false);
    }
  };

  // Handler for promoting a specific iteration's persona versions (for best iteration recommendation)
  const handlePromoteBestIteration = async (iterationNumber) => {
    if (!window.confirm(`This will promote the persona versions from iteration ${iterationNumber} (${loop.metrics?.recommendedQuality?.toFixed(1)}% quality) to live.\n\nThe backend will automatically restore the best iteration's prompt before promoting. Continue?`)) {
      return;
    }
    try {
      setError(null);
      await promoteRLLoopPersonas(loopId, [], token);
      fetchData();
    } catch (err) {
      setError(err.error || 'Failed to promote best iteration');
    }
  };

  // Edit particular focus text handler
  const handleOpenEditFocus = () => {
    setEditFocusText(loop?.config?.reviewConfig?.particularFocusText || '');
    setEditFocusOpen(true);
  };

  const handleSaveEditFocus = async () => {
    setEditFocusSaving(true);
    try {
      await updateRLLoopConfig(loopId, { particularFocusText: editFocusText.trim() || null }, token);
      setEditFocusOpen(false);
      fetchData();
    } catch (err) {
      setError(err.error || 'Failed to update focus text');
    } finally {
      setEditFocusSaving(false);
    }
  };

  // Clone handler
  const handleClone = () => {
    if (onClone && loop) {
      onClone(loop.config, loop.name);
    }
  };

  const getTrendIcon = () => {
    if (!loop?.metrics?.qualityTrend || loop.metrics.qualityTrend.length < 2) {
      return <TrendingFlatIcon color="action" />;
    }
    const trend = loop.metrics.qualityTrend;
    const recent = trend.slice(-2);
    const diff = recent[1] - recent[0];
    if (diff > 1) return <TrendingUpIcon color="success" />;
    if (diff < -1) return <TrendingDownIcon color="error" />;
    return <TrendingFlatIcon color="action" />;
  };

  const getChartData = () => {
    if (!loop?.metrics?.qualityTrend) return [];
    return loop.metrics.qualityTrend.map((score, i) => ({
      iteration: i + 1,
      quality: score
    }));
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading loop data...</Typography>
      </Box>
    );
  }

  if (!loop) {
    return (
      <Alert severity="error">Loop not found</Alert>
    );
  }

  const canPause = loop.status === 'running';
  const canResume = ['paused_by_user', 'paused_for_code_change'].includes(loop.status);
  const canCancel = ['running', 'paused_by_user', 'paused_for_code_change'].includes(loop.status);
  const canPromote = loop.status === 'completed' && !loop.hasPromotedPersonas;

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Auto-Resume Notification */}
      {loop.autoResumeCount > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Auto-Resumed:</strong> This loop was automatically resumed {loop.autoResumeCount} time(s) after server restart.
            {loop.lastAutoResumedAt && (
              <span> Last resumed: {new Date(loop.lastAutoResumedAt).toLocaleString()}</span>
            )}
          </Typography>
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h5">{loop.name}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Chip
              label={STATUS_LABELS[loop.status]}
              color={STATUS_COLORS[loop.status]}
              size="small"
            />
            {refreshing && <Typography variant="caption" color="text.secondary">Refreshing...</Typography>}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="View Sandbox Prompt">
            <Button
              variant="outlined"
              size="small"
              startIcon={<ViewIcon />}
              onClick={handleViewPrompt}
            >
              View Prompt
            </Button>
          </Tooltip>
          {onClone && (
            <Tooltip title="Clone this loop's configuration">
              <Button
                variant="outlined"
                size="small"
                startIcon={<CloneIcon />}
                onClick={handleClone}
              >
                Clone
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Refresh">
            <IconButton onClick={() => { setRefreshing(true); fetchData(); }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          {canPause && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<PauseIcon />}
              onClick={() => setPauseDialogOpen(true)}
            >
              Pause
            </Button>
          )}
          {canResume && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<ResumeIcon />}
              onClick={() => setResumeDialogOpen(true)}
            >
              Resume
            </Button>
          )}
          {canCancel && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
              onClick={handleCancel}
            >
              Cancel
            </Button>
          )}
          {canPromote && (
            <Button
              variant="contained"
              color="success"
              startIcon={<PromoteIcon />}
              onClick={() => setPromoteDialogOpen(true)}
            >
              Promote to Live
            </Button>
          )}
          {canPromote && (
            <Button
              variant="contained"
              color="info"
              startIcon={<SaveAsNewIcon />}
              onClick={handleOpenSaveAsNew}
            >
              Save as New Persona
            </Button>
          )}
        </Box>
      </Box>

      {/* Pause Context Alert */}
      {loop.pauseContext && ['paused_by_user', 'paused_for_code_change'].includes(loop.status) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Paused: {loop.pauseContext.pauseReason}</Typography>
          {loop.pauseContext.suggestedCodeChanges?.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2">Suggested code changes:</Typography>
              <ul>
                {loop.pauseContext.suggestedCodeChanges.map((change, i) => (
                  <li key={i}>
                    <strong>[{change.priority}] {change.fileType}:</strong> {change.description}
                  </li>
                ))}
              </ul>
            </Box>
          )}
        </Alert>
      )}

      {/* Progress */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Progress: Iteration {loop.progress.currentIteration} of {loop.config.maxIterations}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={loop.completionPercentage || 0}
            sx={{ mt: 1, height: 8, borderRadius: 4 }}
          />
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <Typography variant="caption" color="text.secondary">Completed Runs</Typography>
            <Typography variant="h6">{loop.progress.completedRuns}</Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="caption" color="text.secondary">Failed Runs</Typography>
            <Typography variant="h6" color={loop.progress.failedRuns > 0 ? 'error' : 'inherit'}>
              {loop.progress.failedRuns}
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="caption" color="text.secondary">Total Cost</Typography>
            <Typography variant="h6">${loop.costs.totalCost?.toFixed(4) || '0'}</Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="caption" color="text.secondary">Quality Target</Typography>
            <Typography variant="h6">{loop.config.qualityThreshold}%</Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Metrics */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2" color="text.secondary">Current Quality</Typography>
                {getTrendIcon()}
              </Box>
              <Typography variant="h4">
                {loop.metrics.currentQuality?.toFixed(1) || '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Started at {loop.metrics.startingQuality?.toFixed(1) || '—'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Best Quality</Typography>
              <Typography variant="h4">
                {loop.metrics.bestQuality?.toFixed(1) || '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Iteration {loop.metrics.bestIterationNumber || '—'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Improvement Rate</Typography>
              <Typography variant="h4" color={loop.metrics.improvementRate > 0 ? 'success.main' : 'text.primary'}>
                {loop.metrics.improvementRate ? `${(loop.metrics.improvementRate * 100).toFixed(1)}%` : '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                From baseline
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quality Trend Chart */}
      {getChartData().length > 1 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>Quality Trend</Typography>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={getChartData()}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="iteration" label={{ value: 'Iteration', position: 'bottom' }} />
              <YAxis domain={[0, 100]} label={{ value: 'Quality', angle: -90, position: 'left' }} />
              <ChartTooltip />
              <Line type="monotone" dataKey="quality" stroke="#1976d2" strokeWidth={2} dot />
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* Loop Configuration */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ConfigIcon fontSize="small" />
            <Typography variant="subtitle1">Loop Configuration</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            {/* Target Personas */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Target Personas</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(loop.config?.targetPersonas || []).map((p, i) => (
                  <Chip key={i} label={`${p.personaKey} (${p.personaType})`} size="small" variant="outlined" />
                ))}
              </Box>
            </Grid>

            {/* Client Personas */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Client Personas</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(loop.config?.simulationConfig?.clientPersonaKeys || []).map((key, i) => (
                  <Chip key={i} label={key} size="small" variant="outlined" />
                ))}
              </Box>
            </Grid>

            {/* Simulation Settings */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" gutterBottom>Simulation Settings</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="caption" color="text.secondary">Max Turns</Typography>
              <Typography variant="body2">{loop.config?.simulationConfig?.maxTurns || '—'}</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="caption" color="text.secondary">Runs/Iteration</Typography>
              <Typography variant="body2">{loop.config?.simulationConfig?.runsPerIteration || '—'}</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="caption" color="text.secondary">Simulation LLM</Typography>
              <Typography variant="body2">
                {loop.config?.simulationConfig?.llmConfig?.provider || 'vertex_ai'} / {loop.config?.simulationConfig?.llmConfig?.model || '—'}
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="caption" color="text.secondary">Sim Temperature</Typography>
              <Typography variant="body2">{loop.config?.simulationConfig?.llmConfig?.temperature ?? '—'}</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="caption" color="text.secondary">Parallel Sims</Typography>
              <Typography variant="body2">{loop.config?.simulationConfig?.maxConcurrentSimulations || 5}</Typography>
            </Grid>

            {/* Stop Conditions */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" gutterBottom>Stop Conditions</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="caption" color="text.secondary">Quality Threshold</Typography>
              <Typography variant="body2">{loop.config?.qualityThreshold || '—'}%</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="caption" color="text.secondary">Max Iterations</Typography>
              <Typography variant="body2">{loop.config?.maxIterations || '—'}</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="caption" color="text.secondary">Max Cost</Typography>
              <Typography variant="body2">${loop.config?.stopConditions?.maxCostUSD || '—'}</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="caption" color="text.secondary">Plateau Detection</Typography>
              <Typography variant="body2">{loop.config?.stopConditions?.maxConsecutiveNoImprovement || '—'} iterations</Typography>
            </Grid>

            {/* Improvement Scope */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" gutterBottom>Improvement Scope</Typography>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {loop.config?.improvementScope?.therapistPrompt && <Chip label="Therapist Prompt" size="small" color="primary" variant="outlined" />}
                {loop.config?.improvementScope?.clientPersonaBehavior && <Chip label="Client Behavior" size="small" color="primary" variant="outlined" />}
                {loop.config?.improvementScope?.suggestCodeChanges && <Chip label="Code Changes" size="small" color="primary" variant="outlined" />}
                {loop.config?.improvementScope?.clientPersona && <Chip label="Client Persona Realism" size="small" color="secondary" variant="outlined" />}
              </Box>
            </Grid>

            {/* Review Config */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" gutterBottom>Review Configuration</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="caption" color="text.secondary">Review LLM</Typography>
              <Typography variant="body2">
                {loop.config?.reviewConfig?.llmConfig?.provider || 'vertex_ai'} / {loop.config?.reviewConfig?.llmConfig?.model || '—'}
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="caption" color="text.secondary">Review Temperature</Typography>
              <Typography variant="body2">{loop.config?.reviewConfig?.llmConfig?.temperature ?? '—'}</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="caption" color="text.secondary">Max Suggestions</Typography>
              <Typography variant="body2">{loop.config?.reviewConfig?.maxSuggestionsPerIteration || '—'}</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="caption" color="text.secondary">Focus Areas</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {(loop.config?.reviewConfig?.focusAreas || []).map((area, i) => (
                  <Chip key={i} label={area} size="small" />
                ))}
              </Box>
            </Grid>

            {/* Rollback & Consolidation */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" gutterBottom>Rollback & Consolidation</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="caption" color="text.secondary">Auto-Rollback</Typography>
              <Typography variant="body2">{loop.config?.autoRollbackConfig?.enabled ? 'Enabled' : 'Disabled'}</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="caption" color="text.secondary">Regression Threshold</Typography>
              <Typography variant="body2">{loop.config?.autoRollbackConfig?.regressionThreshold || '—'}%</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="caption" color="text.secondary">Consolidation</Typography>
              <Typography variant="body2">{loop.config?.consolidationConfig?.enabled ? 'Enabled' : 'Disabled'}</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="caption" color="text.secondary">Consolidation Freq</Typography>
              <Typography variant="body2">Every {loop.config?.consolidationConfig?.frequencyIterations || '—'} iterations</Typography>
            </Grid>

            {/* Particular Focus */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2">Areas of Particular Focus</Typography>
                <Tooltip title="Edit focus guidance">
                  <IconButton size="small" onClick={handleOpenEditFocus}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              {loop.config?.reviewConfig?.particularFocusText ? (
                <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, bgcolor: 'action.hover' }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {loop.config.reviewConfig.particularFocusText}
                  </Typography>
                </Paper>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                  No particular focus set. Click edit to add guidance for the reviewer.
                </Typography>
              )}
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Failed Improvements Alert */}
      {loop.failedImprovementsHistory?.length > 0 && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'warning.light' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <FailedIcon color="warning" />
            <Typography variant="subtitle2">
              {loop.failedImprovementsHistory.length} Failed Improvement(s) Recorded
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            These improvements caused quality regressions and are being avoided in future iterations.
          </Typography>
        </Paper>
      )}

      {/* Promotion Recommendation Banner - shown when best iteration != final iteration */}
      {loop.metrics?.promotionRecommended && !loop.hasPromotedPersonas && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              onClick={() => handlePromoteBestIteration(loop.metrics.recommendedIterationNumber)}
            >
              Promote Iteration {loop.metrics.recommendedIterationNumber}
            </Button>
          }
        >
          <AlertTitle>Best Iteration Recommendation</AlertTitle>
          <Typography variant="body2">
            Iteration {loop.metrics.recommendedIterationNumber} achieved <strong>{loop.metrics.recommendedQuality?.toFixed(1)}%</strong> review quality
            — better than the final iteration's <strong>{loop.metrics.currentQuality?.toFixed(1)}%</strong>.
            Consider promoting the prompts from iteration {loop.metrics.recommendedIterationNumber} instead.
          </Typography>
        </Alert>
      )}

      {/* Session Summary (shown when loop is completed/stopped) */}
      <SessionSummary loop={loop} iterations={iterations} token={token} />

      {/* Iterations List */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>Iterations</Typography>
        <List>
          {iterations.map((iter) => (
            <React.Fragment key={iter.iterationNumber}>
              <ListItem
                button
                onClick={() => setExpandedIteration(
                  expandedIteration === iter.iterationNumber ? null : iter.iterationNumber
                )}
              >
                <ListItemIcon>
                  {iter.isRollbackIteration ? <RollbackIcon color="warning" /> :
                   iter.isConsolidationPass ? <ConsolidateIcon color="info" /> :
                   iter.status === 'completed' ? <CheckIcon color="success" /> :
                   iter.status === 'failed' ? <ErrorIcon color="error" /> :
                   iter.status === 'paused' ? <WarningIcon color="warning" /> :
                   <RefreshIcon color="primary" />}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>Iteration {iter.iterationNumber}</span>
                      {iter.isRollbackIteration && (
                        <Chip label="Rollback" size="small" color="warning" />
                      )}
                      {iter.isConsolidationPass && (
                        <Chip label="Consolidation" size="small" color="info" />
                      )}
                      {loop.metrics?.promotionRecommended &&
                       loop.metrics?.recommendedIterationNumber === iter.iterationNumber && (
                        <Chip label="⭐ Best" size="small" color="success" />
                      )}
                    </Box>
                  }
                  secondary={`Sim: ${iter.aggregateMetrics?.avgQualityScore?.toFixed(1) || '—'} | Review: ${iter.review?.weightedQualityScore?.toFixed(1) || '—'} | ${iter.appliedImprovements?.length || 0} improvements`}
                />
                {expandedIteration === iter.iterationNumber ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItem>
              <Collapse in={expandedIteration === iter.iterationNumber}>
                <Box sx={{ pl: 4, pr: 2, pb: 2 }}>
                  {/* Rollback Info */}
                  {iter.isRollbackIteration && (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                      <strong>Auto-Rollback:</strong> {iter.rollbackReason}
                      {iter.rollbackFromIteration && (
                        <span> (Reverted to iteration {iter.rollbackFromIteration})</span>
                      )}
                    </Alert>
                  )}

                  {/* Consolidation Info */}
                  {iter.isConsolidationPass && (
                    <Alert severity="info" sx={{ mb: 1 }}>
                      <strong>Consolidation Pass:</strong> Prompt was rewritten to remove cruft and organize structure.
                    </Alert>
                  )}

                  {/* Reverted Improvements */}
                  {iter.revertedImprovements?.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" color="error.main">
                        <strong>Reverted Improvements:</strong>
                      </Typography>
                      {iter.revertedImprovements.map((rev, i) => (
                        <Chip
                          key={i}
                          label={`${rev.improvementDescription?.substring(0, 40)}... (${rev.qualityDropAmount?.toFixed(1)}% drop)`}
                          size="small"
                          color="error"
                          variant="outlined"
                          sx={{ m: 0.5 }}
                        />
                      ))}
                    </Box>
                  )}

                  {iter.review?.evaluation && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Evaluation:</strong>
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {iter.review.evaluation}
                        </Typography>
                      </Paper>
                    </Box>
                  )}

                  {/* Quality Dimension Breakdown */}
                  <QualityDimensionBreakdown
                    qualityBreakdown={iter.review?.qualityBreakdown}
                    scoringScale={iter.review?.scoringScale || 3}
                  />

                  {/* Recommendations and Next Iteration Focus */}
                  <RecommendationsSection
                    recommendations={iter.review?.recommendations}
                    nextIterationFocus={iter.review?.nextIterationFocus}
                  />

                  {/* Client Persona Realism: Calibration Issues */}
                  <CalibrationIssuesSection
                    calibrationIssues={iter.review?.calibrationIssues}
                  />

                  {/* Client Persona Realism: Improvement Suggestions */}
                  <PersonaImprovementsSection
                    personaImprovements={iter.review?.personaImprovements}
                  />

                  {iter.appliedImprovements?.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2"><strong>Applied Improvements:</strong> (click for details)</Typography>
                      {iter.appliedImprovements.map((imp, i) => (
                        <Chip
                          key={i}
                          label={`${imp.personaKey}: ${imp.changeDescription?.substring(0, 50)}...`}
                          size="small"
                          sx={{ m: 0.5, cursor: 'pointer' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedImprovement(imp);
                          }}
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  )}
                  {iter.review?.safetyFlags?.length > 0 && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      {iter.review.safetyFlags.length} safety flag(s) detected
                    </Alert>
                  )}
                </Box>
              </Collapse>
              <Divider />
            </React.Fragment>
          ))}
        </List>
      </Paper>

      {/* Pause Dialog */}
      <Dialog open={pauseDialogOpen} onClose={() => setPauseDialogOpen(false)}>
        <DialogTitle>Pause RL Loop</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Reason for pausing"
            value={pauseReason}
            onChange={(e) => setPauseReason(e.target.value)}
            multiline
            rows={2}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPauseDialogOpen(false)}>Cancel</Button>
          <Button onClick={handlePause} variant="contained" color="warning">Pause</Button>
        </DialogActions>
      </Dialog>

      {/* Resume Dialog */}
      <Dialog open={resumeDialogOpen} onClose={() => setResumeDialogOpen(false)}>
        <DialogTitle>Resume RL Loop</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Describe any changes you made while the loop was paused (e.g., code changes, manual prompt edits).
          </Typography>
          <TextField
            fullWidth
            label="Notes about changes made"
            value={resumeNotes}
            onChange={(e) => setResumeNotes(e.target.value)}
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResumeDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleResume} variant="contained" color="primary">Resume</Button>
        </DialogActions>
      </Dialog>

      {/* Promote Dialog */}
      <Dialog open={promoteDialogOpen} onClose={() => setPromoteDialogOpen(false)}>
        <DialogTitle>Promote Personas to Live</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will copy the improved prompts from sandbox to your live personas.
            This action affects production!
          </Alert>
          {loop.metrics?.promotionRecommended ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              The best quality was achieved at iteration {loop.metrics.bestIterationNumber} ({loop.metrics.bestQuality?.toFixed(1)}%).
              This version will be automatically restored and promoted.
            </Alert>
          ) : null}
          <Typography variant="body2">
            {loop.metrics?.promotionRecommended
              ? <>Best quality score: <strong>{loop.metrics.bestQuality?.toFixed(1)}%</strong> (iteration {loop.metrics.bestIterationNumber})</>
              : <>Final quality score: <strong>{loop.metrics.currentQuality?.toFixed(1)}%</strong></>
            }
          </Typography>
          <Typography variant="body2">
            Improvement from baseline: <strong>{((loop.metrics.improvementRate || 0) * 100).toFixed(1)}%</strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPromoteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handlePromote} variant="contained" color="success">
            Confirm Promotion
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save as New Persona Dialog */}
      <Dialog open={saveAsNewDialogOpen} onClose={() => setSaveAsNewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Save Improved Prompt as New Persona</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            This creates a new therapist persona with the RL-improved prompt. The original persona remains unchanged.
          </Alert>
          {loop.metrics?.promotionRecommended ? (
            <Alert severity="info" sx={{ mb: 1 }}>
              The best quality was achieved at iteration {loop.metrics.bestIterationNumber} ({loop.metrics.bestQuality?.toFixed(1)}%).
              This version will be used for the new persona.
            </Alert>
          ) : null}
          <Typography variant="body2" sx={{ mb: 1 }}>
            {loop.metrics?.promotionRecommended
              ? <>Best quality score: <strong>{loop.metrics.bestQuality?.toFixed(1)}%</strong> (iteration {loop.metrics.bestIterationNumber})</>
              : <>Final quality score: <strong>{loop.metrics.currentQuality?.toFixed(1)}%</strong></>
            }
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Improvement from baseline: <strong>{((loop.metrics.improvementRate || 0) * 100).toFixed(1)}%</strong>
          </Typography>
          <TextField
            label="Persona Key"
            value={saveAsNewKey}
            onChange={(e) => { setSaveAsNewKey(e.target.value.toLowerCase()); setSaveAsNewError(''); }}
            fullWidth
            required
            margin="normal"
            placeholder="e.g., mbt-specialist-v2"
            helperText="Lowercase letters, numbers, and hyphens only. Min 3 characters."
          />
          <TextField
            label="Persona Name"
            value={saveAsNewName}
            onChange={(e) => { setSaveAsNewName(e.target.value); setSaveAsNewError(''); }}
            fullWidth
            required
            margin="normal"
            placeholder="e.g., MBT Specialist V2"
            helperText="Min 2 characters."
          />
          <TextField
            label="Description"
            value={saveAsNewDescription}
            onChange={(e) => setSaveAsNewDescription(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            rows={2}
          />
          {saveAsNewError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {saveAsNewError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveAsNewDialogOpen(false)} disabled={saveAsNewLoading}>Cancel</Button>
          <Button
            onClick={handleSaveAsNew}
            variant="contained"
            color="info"
            disabled={saveAsNewLoading}
          >
            {saveAsNewLoading ? 'Saving...' : 'Save as New Persona'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Improvement Detail Dialog */}
      <ImprovementDetailDialog
        open={!!selectedImprovement}
        onClose={() => setSelectedImprovement(null)}
        improvement={selectedImprovement}
      />

      {/* Prompt Comparison Dialog */}
      <PromptComparisonDialog
        open={promptComparisonOpen}
        onClose={() => setPromptComparisonOpen(false)}
        comparisons={promptComparisons}
        loopName={loop?.name}
        loading={promptComparisonLoading}
        onViewVersionHistory={handleViewVersionHistory}
        token={token}
      />

      {/* Version History Dialog */}
      <VersionHistoryDialog
        open={versionHistoryOpen}
        onClose={() => {
          setVersionHistoryOpen(false);
          setVersionHistoryPersona(null);
        }}
        personaId={versionHistoryPersona?.personaId}
        personaType={versionHistoryPersona?.personaType}
        personaKey={versionHistoryPersona?.personaKey}
        token={token}
        onRollbackSuccess={handleRollbackSuccess}
      />

      {/* Edit Focus Dialog */}
      <Dialog open={editFocusOpen} onClose={() => setEditFocusOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Areas of Particular Focus</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Provide specific guidance for the reviewer. Changes take effect on the next iteration.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={4}
            maxRows={8}
            label="Particular Focus Guidance"
            placeholder="e.g., Pay particular attention to how the therapist responds to client minimization of substance use..."
            value={editFocusText}
            onChange={(e) => {
              if (e.target.value.length <= 2000) {
                setEditFocusText(e.target.value);
              }
            }}
            helperText={`${editFocusText.length}/2000 characters`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditFocusOpen(false)} disabled={editFocusSaving}>Cancel</Button>
          <Button
            onClick={handleSaveEditFocus}
            variant="contained"
            disabled={editFocusSaving}
          >
            {editFocusSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default RLLoopMonitor;
